import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { downloadPDF, openPDF, THEME_ASSETS } from "../lib/pdf";
import { Logo } from "../components/Logo";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { FileText, FlaskConical, Download, Stethoscope, ShieldCheck, Printer, PenLine, CheckCircle2 } from "lucide-react";

export default function SharePrescription() {
  const { shareId } = useParams();
  const [rx, setRx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/share/${shareId}`).then((r) => setRx(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [shareId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-10 h-10 rounded-full border-4 border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>;
  if (error || !rx) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
      <Logo size={48} /><p className="text-muted-foreground">Documento no encontrado o expirado.</p>
    </div>
  );

  const isStudy = rx.kind === "estudio";
  const isConsent = rx.kind === "consentimiento";
  const isOdontogram = rx.kind === "odontograma";
  const isGeneric = Array.isArray(rx.sections) && rx.sections.length > 0;
  const theme = THEME_ASSETS[rx.theme] || THEME_ASSETS.clasica;
  const url = `${window.location.origin}/receta/${shareId}`;
  const title = isOdontogram ? (rx.share_title || "Odontograma") : isGeneric ? (rx.title || "Documento clínico") : isConsent ? "Consentimiento informado" : isStudy ? "Solicitud de estudios" : "Receta médica";
  const TitleIcon = isConsent ? ShieldCheck : isStudy ? FlaskConical : FileText;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><Logo size={42} /></div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(61,64,91,0.08)] border border-stone-100 overflow-hidden relative">
          {theme.img ? (
            <img src={theme.img} alt="" className="w-full h-20 object-cover" />
          ) : (
            <div className="h-1.5 bg-brand-terracotta" />
          )}
          {theme.img && <img src={theme.img} alt="" className="pointer-events-none absolute inset-x-6 top-28 opacity-[0.05]" />}
          <div className="p-6 sm:p-8 relative">
            <div className="flex items-center gap-3 pb-4 border-b border-stone-100">
              <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center text-brand-terracotta shrink-0"><Stethoscope size={24} /></div>
              <div className="min-w-0">
                <p className="font-outfit font-bold text-brand-ink">{rx.doctor_name}</p>
                <p className="text-xs text-muted-foreground">{[rx.doctor_specialty, rx.doctor_university].filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-muted-foreground">{[rx.doctor_cedula && `Céd. Prof. ${rx.doctor_cedula}`, rx.doctor_specialty_cedula && `Céd. Esp. ${rx.doctor_specialty_cedula}`, rx.doctor_rfc && `RFC ${rx.doctor_rfc}`].filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-muted-foreground">{[rx.clinic_name, rx.doctor_address, rx.doctor_phone].filter(Boolean).join(" · ")}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5 mb-1">
              <TitleIcon className="text-brand-terracotta" size={20} />
              <h1 className="font-outfit text-xl font-extrabold text-brand-ink">{title}</h1>
            </div>
            <p className="text-sm text-muted-foreground">{rx.patient_name} · {rx.patient_age} · {new Date(rx.created_at).toLocaleDateString("es-MX")}{rx.folio ? ` · Folio ${rx.folio}` : ""}</p>
            {rx.diagnosis && <p className="text-sm mt-2 text-brand-ink"><b>Diagnóstico:</b> {rx.diagnosis}</p>}

            {isOdontogram ? (
              <div className="mt-5" data-testid="shared-odontogram">
                {rx.odontogram_image && <img src={rx.odontogram_image} alt={title} className="w-full rounded-2xl border border-stone-100" />}
                {rx.dentition && <p className="text-xs text-muted-foreground mt-2">Dentición: {({ temporal: "Temporal", mixta: "Mixta", permanente: "Permanente" }[rx.dentition]) || "—"}</p>}
                {rx.notes && <p className="text-sm text-brand-ink/80 bg-stone-50 rounded-2xl p-4 mt-3">{rx.notes}</p>}
              </div>
            ) : isConsent ? (
              <div className="mt-5 space-y-3 text-sm">
                {rx.procedure && <p className="text-brand-ink"><b>Procedimiento:</b> {rx.procedure}</p>}
                {rx.consent_text && <p className="text-brand-ink/80 bg-stone-50 rounded-2xl p-4">{rx.consent_text}</p>}
                {[["Beneficios", rx.benefits], ["Riesgos", rx.risks], ["Alternativas", rx.alternatives]].map(([t, arr]) => (
                  arr?.length > 0 && (
                    <div key={t}>
                      <p className="text-xs font-bold uppercase tracking-wide text-brand-sage mb-1">{t}</p>
                      <ul className="list-disc pl-5 text-brand-ink/80 space-y-0.5">{arr.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </div>
                  )
                ))}
              </div>
            ) : isGeneric ? (
              <div className="mt-5 space-y-4" data-testid="shared-generic">
                {rx.sections.map((s, si) => (
                  <div key={si} className="bg-stone-50 rounded-2xl p-4">
                    {s.heading && <p className="text-xs font-bold uppercase tracking-wide text-brand-sage mb-2">{s.heading}</p>}
                    {s.text && <p className="text-sm text-brand-ink/80 whitespace-pre-line mb-2">{s.text}</p>}
                    {Array.isArray(s.items) && s.items.length > 0 && (
                      <ul className="list-disc pl-5 text-brand-ink/80 space-y-1.5 text-sm">{s.items.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                {(rx.items || []).map((it, i) => (
                  <div key={i} className="bg-stone-50 rounded-2xl p-4">
                    <p className="font-outfit font-semibold text-brand-ink">{i + 1}. {isStudy ? (it.study || it.name) : `${it.name || ""}${it.presentation ? " " + it.presentation : ""}`}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{isStudy ? it.indication : [(/(oral|v\.?o\.?|boca|tomar)/i.test(it.route || "") || !it.route) ? "Tomar" : "Aplicar", it.dose, it.frequency, it.duration && (/^por/i.test(it.duration) ? it.duration : `por ${it.duration}`)].filter(Boolean).join(" ")}</p>
                  </div>
                ))}
                {rx.indications && (
                  <div className="bg-brand-sage/5 rounded-2xl p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-sage mb-1">Indicaciones</p>
                    <p className="text-sm text-brand-ink/80 whitespace-pre-line">{rx.indications}</p>
                  </div>
                )}
              </div>
            )}

            {isConsent && <ConsentSignature rx={rx} shareId={shareId} onSigned={(s) => setRx((r) => ({ ...r, ...s }))} />}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button data-testid="download-shared-pdf" onClick={() => downloadPDF(rx, url)}
                className="flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.98]">
                <Download size={18} /> Descargar
              </button>
              <button data-testid="print-shared-pdf" onClick={() => openPDF(rx, url)}
                className="flex items-center justify-center gap-2 bg-brand-ink text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.98]">
                <Printer size={18} /> Abrir / Imprimir
              </button>
            </div>
          </div>
        </motion.div>
        <p className="text-center text-xs text-muted-foreground mt-4">Documento generado con ClinPed · NOM-004-SSA3-2012</p>
      </div>
      <Toaster />
    </div>
  );
}

function ConsentSignature({ rx, shareId, onSigned }) {
  const [sig, setSig] = useState("");
  const [name, setName] = useState(rx.guardian_name || "");
  const [rel, setRel] = useState("");
  const [saving, setSaving] = useState(false);

  if (rx.guardian_signature) {
    return (
      <div className="mt-6 border-t border-stone-100 pt-5" data-testid="consent-signed">
        <div className="flex items-center gap-2 text-brand-sageDark mb-3"><CheckCircle2 size={18} /><p className="font-outfit font-bold text-sm">Consentimiento firmado</p></div>
        <div className="bg-stone-50 rounded-2xl p-4 text-center">
          <img src={rx.guardian_signature} alt="Firma" className="h-20 mx-auto object-contain" />
          <div className="border-t border-stone-300 mt-2 pt-2">
            <p className="font-semibold text-brand-ink text-sm">{rx.signer_name}</p>
            <p className="text-xs text-muted-foreground">{[rx.signer_relationship, rx.signed_at && new Date(rx.signed_at).toLocaleString("es-MX")].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!sig) { toast.error("Dibuja tu firma"); return; }
    if (!name.trim()) { toast.error("Escribe tu nombre completo"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/share/${shareId}/sign`, { signature: sig, signer_name: name, relationship: rel });
      toast.success("Consentimiento firmado ✓");
      onSigned(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo firmar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 border-t border-stone-100 pt-5" data-testid="consent-sign-form">
      <div className="flex items-center gap-2 text-brand-terracotta mb-1"><PenLine size={18} /><p className="font-outfit font-bold text-sm">Firma de aceptación</p></div>
      <p className="text-xs text-muted-foreground mb-3">He leído y comprendido la información. Firma con tu dedo para aceptar.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input data-testid="consent-signer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre completo de quien firma"
          className="px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
        <input data-testid="consent-signer-rel" value={rel} onChange={(e) => setRel(e.target.value)} placeholder="Parentesco (madre, padre, tutor…)"
          className="px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
      </div>
      <SignaturePad value={sig} onChange={setSig} />
      <button data-testid="consent-sign-submit" onClick={submit} disabled={saving}
        className="mt-3 w-full flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-60">
        <PenLine size={17} /> {saving ? "Firmando…" : "Firmar y aceptar"}
      </button>
    </div>
  );
}
